import { AlertTriangle, Clock } from 'lucide-react'
import type { Donation } from '@/features/donations/types/donation'
import { Badge } from '@/components/custom/badge'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { DonationCard } from '@/features/donations/components/DonationCard'
import NotifyUnmatchDonationsButton from '@/features/receipts/components/NotifyUnmatchDonationsButton'
import { useIsMobile } from '@/hooks'

type PendingDonationsProps = {
  donations: Array<Donation>
  donorNameResolver: (donorId: string | null) => string
  onVerifyClick: (donationId: string) => void
}

export function PendingDonations({
  donations,
  donorNameResolver,
  onVerifyClick,
}: PendingDonationsProps) {
  const isMobile = useIsMobile()

  if (!isMobile) {
    return (
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">
                Pending Donations
              </h2>
            </div>
            <div>
              <Badge color="yellow">{donations.length} pending</Badge>
              <NotifyUnmatchDonationsButton />
            </div>
          </div>
        </div>
        <div className="p-6">
          {donations.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No pending donations</p>
            </div>
          ) : (
            <ScrollArea className="w-full overflow-x-auto">
              <div className="inline-flex gap-4 w-[75vw]">
                {donations.map((donation) => (
                  <DonationCard
                    key={donation._id}
                    donation={donation}
                    type="pending"
                    donorName={donorNameResolver(donation.donorId)}
                    onVerifyClick={onVerifyClick}
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
      </div>
    )
  }

  return donations.length === 0 ? (
    <div className="text-center py-8">
      <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
      <p className="text-gray-500">No pending donations</p>
    </div>
  ) : (
    <ScrollArea className="flex-1">
      <div className="mb-4">
        <NotifyUnmatchDonationsButton />
      </div>
      <div className="space-y-3">
        {donations.map((donation) => (
          <DonationCard
            key={donation._id}
            donation={donation}
            type="pending"
            donorName={donorNameResolver(donation.donorId)}
            onVerifyClick={onVerifyClick}
          />
        ))}
      </div>
    </ScrollArea>
  )
}
